import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  DollarSign, AlertCircle, TrendingUp, Plus, RefreshCw,
  Check, X, User, Calendar, Filter, MessageCircle, Settings, FileDown,
} from 'lucide-react';
import { buildReceivablesReportHtml } from '@/lib/receivablesReport';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const STATUS_LABELS = {
  pendente: { label: 'Pendente', color: 'bg-blue-100 text-blue-800' },
  parcial: { label: 'Parcial', color: 'bg-amber-100 text-amber-800' },
  vencido: { label: 'Vencido', color: 'bg-rose-100 text-rose-800' },
  pago: { label: 'Pago', color: 'bg-emerald-100 text-emerald-800' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600' },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};

const ContasReceberPage = () => {
  const [itens, setItens] = useState([]);
  const [resumo, setResumo] = useState({ pendente: {}, vencido: {}, recebido_mes: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [customers, setCustomers] = useState([]);

  const [formCreate, setFormCreate] = useState({
    customer_id: '',
    valor_original: '',
    data_vencimento: '',
    descricao: '',
    observacoes: '',
  });

  const [formPay, setFormPay] = useState({
    valor: '',
    forma_pagamento: 'dinheiro',
    observacao: '',
  });

  // Cobrança automática WhatsApp
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    days_before_due: [3, 1],
    days_after_due: [1, 3, 7, 15],
    message_template: '',
    throttle_hours: 24,
    send_hour: 10,
  });
  const [sendingId, setSendingId] = useState(null);
  const [reportFilters, setReportFilters] = useState({ de: '', ate: '', customer_id: '' });
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const gerarRelatorioPdf = async () => {
    setGerandoPdf(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (reportFilters.de) params.set('de', reportFilters.de);
      if (reportFilters.ate) params.set('ate', reportFilters.ate);
      if (reportFilters.customer_id) params.set('customer_id', reportFilters.customer_id);
      const r = await api.get(`/receivables/relatorio?${params.toString()}`);
      const w = window.open('', '_blank', 'width=1000,height=900');
      if (!w) { toast.error('Permita pop-ups para gerar o PDF'); return; }
      w.document.write(buildReceivablesReportHtml(r.data));
      w.document.close();
      toast.success('Relatório gerado — use "Salvar em PDF" na janela aberta');
    } catch (e) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setGerandoPdf(false);
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, resumoResp] = await Promise.all([
        api.get(`/receivables${statusFilter ? `?status=${statusFilter}` : ''}`),
        api.get('/receivables/resumo'),
      ]);
      setItens(listResp.data.itens || []);
      setResumo(resumoResp.data || {});
    } catch (e) {
      toast.error('Erro ao carregar contas a receber');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/customers?limit=500')
      .then((r) => setCustomers(r.data.customers || r.data || []))
      .catch(() => setCustomers([]));
  }, []);

  const criar = async () => {
    if (!formCreate.customer_id || !formCreate.valor_original || !formCreate.data_vencimento) {
      toast.error('Cliente, valor e data de vencimento são obrigatórios');
      return;
    }
    try {
      const cust = customers.find((c) => c.id === formCreate.customer_id);
      await api.post('/receivables', {
        customer_id: formCreate.customer_id,
        customer_name: cust?.name,
        valor_original: Number(formCreate.valor_original),
        data_vencimento: formCreate.data_vencimento,
        descricao: formCreate.descricao,
        observacoes: formCreate.observacoes,
      });
      toast.success('Conta a receber criada');
      setShowCreate(false);
      setFormCreate({ customer_id: '', valor_original: '', data_vencimento: '', descricao: '', observacoes: '' });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar');
    }
  };

  const pagar = async () => {
    if (!formPay.valor || Number(formPay.valor) <= 0) {
      toast.error('Informe o valor do pagamento');
      return;
    }
    try {
      await api.post(`/receivables/${showPay.id}/pagar`, {
        valor: Number(formPay.valor),
        forma_pagamento: formPay.forma_pagamento,
        observacao: formPay.observacao,
      });
      toast.success('Pagamento registrado');
      setShowPay(null);
      setFormPay({ valor: '', forma_pagamento: 'dinheiro', observacao: '' });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar esta conta a receber?')) return;
    try {
      await api.delete(`/receivables/${id}`);
      toast.success('Conta cancelada');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const cobrarWhatsApp = async (rec) => {
    setSendingId(rec.id);
    try {
      const r = await api.post(`/receivables/${rec.id}/send-reminder`);
      if (r.data?.success) {
        toast.success(`Cobrança enviada para ${r.data.customer_phone}`);
      } else {
        toast.error(r.data?.error || 'Falha ao enviar');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar cobrança');
    } finally {
      setSendingId(null);
    }
  };

  const openSettings = async () => {
    try {
      const r = await api.get('/receivables/billing-settings');
      setSettings({
        enabled: !!r.data.enabled,
        days_before_due: r.data.days_before_due || [3, 1],
        days_after_due: r.data.days_after_due || [1, 3, 7, 15],
        message_template: r.data.message_template || '',
        throttle_hours: r.data.throttle_hours ?? 24,
        send_hour: r.data.send_hour ?? 10,
      });
    } catch (_) { /* usa defaults */ }
    setShowSettings(true);
  };

  const salvarSettings = async () => {
    try {
      await api.put('/receivables/billing-settings', {
        enabled: settings.enabled,
        days_before_due: (settings.days_before_due || []).map(Number).filter((n) => n >= 0 && n <= 90),
        days_after_due: (settings.days_after_due || []).map(Number).filter((n) => n >= 0 && n <= 365),
        message_template: settings.message_template,
        throttle_hours: Number(settings.throttle_hours) || 24,
        send_hour: Number(settings.send_hour) || 10,
      });
      toast.success('Configurações de cobrança salvas');
      setShowSettings(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar configurações');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="contas-receber-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contas a Receber
          </h1>
          <p className="text-slate-600">Vendas a prazo (fiado), acompanhamento e recebimento</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openSettings} data-testid="btn-open-settings">
            <Settings className="w-4 h-4 mr-2" /> Cobrança automática
          </Button>
          <Button variant="outline" onClick={carregar} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="btn-create-receivable"
          >
            <Plus className="w-4 h-4 mr-2" /> Nova conta
          </Button>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card data-testid="card-pendente">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-500" /> A receber (pendente)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {formatCurrency(resumo.pendente?.total || 0)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {resumo.pendente?.count || 0} conta(s) em aberto
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-vencido">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500" /> Vencidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-rose-600">
              {formatCurrency(resumo.vencido?.total || 0)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {resumo.vencido?.count || 0} conta(s) atrasada(s)
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-recebido-mes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Recebido este mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">
              {formatCurrency(resumo.recebido_mes?.total || 0)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {resumo.recebido_mes?.count || 0} pagamento(s)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6 flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <Label>Status:</Label>
          </div>
          <Select value={statusFilter || 'todos'} onValueChange={(v) => setStatusFilter(v === 'todos' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="filter-status">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="parcial">Parcial</SelectItem>
              <SelectItem value="vencido">Vencido</SelectItem>
              <SelectItem value="pago">Pago</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <div>
            <Label className="text-xs text-slate-500">Emissão de</Label>
            <Input type="date" value={reportFilters.de} onChange={(e) => setReportFilters({ ...reportFilters, de: e.target.value })} className="w-40" data-testid="report-date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">até</Label>
            <Input type="date" value={reportFilters.ate} onChange={(e) => setReportFilters({ ...reportFilters, ate: e.target.value })} className="w-40" data-testid="report-date-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Cliente</Label>
            <Select value={reportFilters.customer_id || 'todos'} onValueChange={(v) => setReportFilters({ ...reportFilters, customer_id: v === 'todos' ? '' : v })}>
              <SelectTrigger className="w-52" data-testid="report-customer"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os clientes</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={gerarRelatorioPdf} disabled={gerandoPdf} className="ml-auto bg-slate-900 hover:bg-slate-800" data-testid="btn-report-pdf">
            {gerandoPdf ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Relatório PDF
          </Button>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tabela-receivables">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium">Cliente</th>
                <th className="text-left p-3 font-medium">Descrição</th>
                <th className="text-right p-3 font-medium">Valor</th>
                <th className="text-right p-3 font-medium">Pago</th>
                <th className="text-right p-3 font-medium">Restante</th>
                <th className="text-center p-3 font-medium">Vencimento</th>
                <th className="text-center p-3 font-medium">Status</th>
                <th className="text-center p-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500">Carregando...</td></tr>
              ) : itens.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500">
                  Nenhuma conta a receber encontrada. Clique em &quot;Nova conta&quot; para criar.
                </td></tr>
              ) : itens.map((r) => {
                const st = STATUS_LABELS[r.status] || STATUS_LABELS.pendente;
                return (
                  <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`row-receivable-${r.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {r.customer_name}
                      </div>
                    </td>
                    <td className="p-3 text-slate-600">{r.descricao || '-'}</td>
                    <td className="p-3 text-right font-medium">{formatCurrency(r.valor_original)}</td>
                    <td className="p-3 text-right text-emerald-600">{formatCurrency(r.valor_pago)}</td>
                    <td className="p-3 text-right text-blue-600 font-medium">{formatCurrency(r.valor_restante)}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-slate-400" /> {fmtDate(r.data_vencimento)}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={st.color}>{st.label}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        {r.status !== 'pago' && r.status !== 'cancelado' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => cobrarWhatsApp(r)}
                            disabled={sendingId === r.id}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            title="Enviar cobrança via WhatsApp"
                            data-testid={`btn-cobrar-${r.id}`}
                          >
                            <MessageCircle className="w-3 h-3 mr-1" />
                            {sendingId === r.id ? 'Enviando...' : 'Cobrar'}
                          </Button>
                        )}
                        {r.status !== 'pago' && r.status !== 'cancelado' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setShowPay(r); setFormPay({ ...formPay, valor: String(r.valor_restante) }); }}
                            data-testid={`btn-pay-${r.id}`}
                          >
                            <Check className="w-3 h-3 mr-1" /> Receber
                          </Button>
                        )}
                        {r.status !== 'pago' && r.status !== 'cancelado' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancelar(r.id)}
                            data-testid={`btn-cancel-${r.id}`}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="modal-create-receivable">
          <DialogHeader><DialogTitle>Nova conta a receber</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Cliente *</Label>
              <Select
                value={formCreate.customer_id}
                onValueChange={(v) => setFormCreate({ ...formCreate, customer_id: v })}
              >
                <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formCreate.valor_original}
                  onChange={(e) => setFormCreate({ ...formCreate, valor_original: e.target.value })}
                  data-testid="input-valor"
                />
              </div>
              <div>
                <Label>Vencimento *</Label>
                <Input
                  type="date"
                  value={formCreate.data_vencimento}
                  onChange={(e) => setFormCreate({ ...formCreate, data_vencimento: e.target.value })}
                  data-testid="input-vencimento"
                />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                value={formCreate.descricao}
                onChange={(e) => setFormCreate({ ...formCreate, descricao: e.target.value })}
                placeholder="Ex: Venda balcão 123"
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Input
                value={formCreate.observacoes}
                onChange={(e) => setFormCreate({ ...formCreate, observacoes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={criar} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-create">
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Pagar */}
      <Dialog open={!!showPay} onOpenChange={(open) => !open && setShowPay(null)}>
        <DialogContent data-testid="modal-pay-receivable">
          <DialogHeader>
            <DialogTitle>Registrar recebimento</DialogTitle>
          </DialogHeader>
          {showPay && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded">
                <p className="text-sm text-slate-600">Cliente: <strong>{showPay.customer_name}</strong></p>
                <p className="text-sm text-slate-600">Restante: <strong className="text-blue-600">{formatCurrency(showPay.valor_restante)}</strong></p>
              </div>
              <div>
                <Label>Valor recebido *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formPay.valor}
                  onChange={(e) => setFormPay({ ...formPay, valor: e.target.value })}
                  data-testid="input-pay-valor"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Aceita pagamento parcial. Valor máximo: {formatCurrency(showPay.valor_restante)}
                </p>
              </div>
              <div>
                <Label>Forma de pagamento</Label>
                <Select
                  value={formPay.forma_pagamento}
                  onValueChange={(v) => setFormPay({ ...formPay, forma_pagamento: v })}
                >
                  <SelectTrigger data-testid="select-forma-pagamento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de crédito</SelectItem>
                    <SelectItem value="cartao_debito">Cartão de débito</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observação</Label>
                <Input
                  value={formPay.observacao}
                  onChange={(e) => setFormPay({ ...formPay, observacao: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(null)}>Cancelar</Button>
            <Button onClick={pagar} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-pay">
              Registrar pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Configurações Cobrança WhatsApp */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-billing-settings">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              Cobrança automática via WhatsApp
            </DialogTitle>
            <DialogDescription>
              Configure quando e como enviar lembretes de cobrança para clientes com contas próximas ao vencimento ou vencidas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base">Ativar cobrança automática</Label>
                <p className="text-xs text-slate-500 mt-1">
                  O sistema enviará mensagens para clientes com contas próximas ao vencimento ou vencidas.
                </p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                data-testid="switch-enabled"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lembrar ANTES do vencimento (dias)</Label>
                <Input
                  value={(settings.days_before_due || []).join(', ')}
                  onChange={(e) => setSettings({
                    ...settings,
                    days_before_due: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !isNaN(n)),
                  })}
                  placeholder="Ex: 3, 1"
                  data-testid="input-days-before"
                />
                <p className="text-xs text-slate-500 mt-1">Separe por vírgula. Ex: &quot;3, 1&quot; = 3 e 1 dia antes.</p>
              </div>
              <div>
                <Label>Cobrar DEPOIS do vencimento (dias)</Label>
                <Input
                  value={(settings.days_after_due || []).join(', ')}
                  onChange={(e) => setSettings({
                    ...settings,
                    days_after_due: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !isNaN(n)),
                  })}
                  placeholder="Ex: 1, 3, 7, 15"
                  data-testid="input-days-after"
                />
                <p className="text-xs text-slate-500 mt-1">Ex: &quot;1, 3, 7, 15&quot; = após 1, 3, 7 e 15 dias.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Intervalo mínimo entre envios (horas)</Label>
                <Input
                  type="number"
                  min="1"
                  max="720"
                  value={settings.throttle_hours}
                  onChange={(e) => setSettings({ ...settings, throttle_hours: e.target.value })}
                  data-testid="input-throttle"
                />
                <p className="text-xs text-slate-500 mt-1">Evita spam. Padrão: 24h.</p>
              </div>
              <div>
                <Label>Hora do dia para envio (0-23)</Label>
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={settings.send_hour}
                  onChange={(e) => setSettings({ ...settings, send_hour: e.target.value })}
                  data-testid="input-send-hour"
                />
                <p className="text-xs text-slate-500 mt-1">Referência para o robô (padrão 10h).</p>
              </div>
            </div>

            <div>
              <Label>Mensagem (template)</Label>
              <Textarea
                rows={8}
                value={settings.message_template}
                onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
                placeholder="Deixe em branco para usar a mensagem padrão"
                data-testid="input-template"
              />
              <p className="text-xs text-slate-500 mt-1">
                Variáveis: <code>{'{cliente}'}</code>, <code>{'{empresa}'}</code>, <code>{'{descricao}'}</code>,
                <code> {'{valor}'}</code>, <code>{'{vencimento}'}</code>, <code>{'{dias_atraso}'}</code>, <code>{'{situacao}'}</code>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancelar</Button>
            <Button onClick={salvarSettings} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-settings">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasReceberPage;
